import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider, updateProfile } from "firebase/auth";
import toast from "react-hot-toast";
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Share2,
  MoreVertical,
  MapPin,
  Calendar,
  Edit3,
  X,
  Save,
  Building,
  Upload,
  Mail,
  Phone,
  Info,
  Camera,
  Link as LinkIcon,
} from "lucide-react";

const HRNewProfile = () => {
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);
  const storage = getStorage(firebaseApp);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("reviews");
  const [companyHistory, setCompanyHistory] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showManualPictureModal, setShowManualPictureModal] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [imageError, setImageError] = useState(false);
  const fileInputRef = React.useRef(null);
  const bannerInputRef = React.useRef(null);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    username: "",
    title: "",
    location: "",
    company: "",
    bio: "",
    phone: "",
    expertise: "",
  });

  // Load and live-sync HR profile data from resume
  useEffect(() => {
    if (!user?.id) return;

    let initialLoaded = false;
    const unsub = onSnapshot(doc(db, "hrProfiles", user.id), async (profileSnap) => {
      try {
        const profile = profileSnap.exists() ? profileSnap.data() : {};
        const recruiterSnap = await getDoc(doc(db, "recruiters", user.id));
        const recruiter = recruiterSnap.exists() ? recruiterSnap.data() : {};

        const resumeData = profile.resumeData || {};
        const resumeInfo = resumeData.personalInfo || {};
        const professionalInfo = resumeData.professionalInfo || {};

        const data = {
          name: profile.name || resumeInfo.fullName || recruiter.name || user.name || "HR Manager",
          username: profile.username || `hr_${user.id.slice(0, 6)}`,
          title: profile.title || professionalInfo.jobTitle || recruiter.title || "HR Manager",
          company: profile.company || resumeInfo.company || recruiter.company || "Company Name",
          location: profile.location || resumeInfo.city || recruiter.location || "Location not set",
          joinDate: profile.createdAt || recruiter.createdAt || new Date(),
          bio: profile.bio || resumeInfo.summary || "",
          avatar: profile.profilePictureURL || profile.avatar || recruiter.avatar || user.photoURL || null,
          banner: profile.bannerUrl || null,
          email: user.email || "",
          phone: profile.phone || "",
          expertise: professionalInfo.skills || profile.expertise || [],
          resumeData,
        };

        setProfileData(data);
        if (!initialLoaded) {
          setEditFormData({
            name: data.name,
            username: data.username,
            title: data.title,
            location: data.location,
            company: data.company,
            bio: data.bio,
            phone: data.phone,
            expertise: Array.isArray(data.expertise) ? data.expertise.join(", ") : data.expertise || "",
          });
          initialLoaded = true;
        }
      } catch (error) {
        console.error("Error syncing HR profile:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [user, db]);

  // Load company history
  useEffect(() => {
    const loadCompanyHistory = async () => {
      if (!user?.id) return;

      try {
        const historyRef = collection(db, "hrCompanyHistory");
        const q = query(
          historyRef,
          where("hrId", "==", user.id),
          orderBy("startDate", "desc"),
          limit(5)
        );

        const snapshot = await getDocs(q);
        const history = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCompanyHistory(history);
      } catch (error) {
        console.error("Error loading company history:", error);
      }
    };

    loadCompanyHistory();
  }, [user, db]);

  // Reset image error when avatar changes
  useEffect(() => {
    setImageError(false);
  }, [profileData?.avatar]);

  // Load reviews data
  useEffect(() => {
    const fetchReviews = async () => {
      if (!user?.id) return;

      try {
        const reviewsRef = collection(db, "hrReviews");
        const q = query(
          reviewsRef,
          where("hrId", "==", user.id),
          orderBy("createdAt", "desc"),
          limit(10)
        );

        const snapshot = await getDocs(q);
        const rev = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setReviews(rev);
      } catch (error) {
        console.error("Error loading reviews:", error);
      }
    };

    fetchReviews();
  }, [user, db]);

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingPicture(true);

    try {
      const storageRef = ref(storage, `profile-pictures/${user.id}/${Date.now()}`);

      const uploadPromise = uploadBytes(storageRef, file);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Upload timed out")), 60000)
      );

      await Promise.race([uploadPromise, timeoutPromise]);
      const downloadURL = await getDownloadURL(storageRef);

      const auth = getAuth(firebaseApp);
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          photoURL: downloadURL,
        });
      }

      await updateDoc(doc(db, "hrProfiles", user.id), {
        profilePictureURL: downloadURL,
      });

      // Sync with recruiters collection (Primary Source)
      try {
        await setDoc(doc(db, "recruiters", user.id), {
          avatar: downloadURL
        }, { merge: true });
      } catch (err) {
        console.error("Failed to sync avatar to recruiters:", err);
      }

      setProfileData((prev) => ({
        ...prev,
        avatar: downloadURL,
      }));

      if (updateUserProfile) {
        updateUserProfile({
          avatar: downloadURL
        });
      }

      toast.success("Profile picture updated successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message === "Upload timed out" ? "Upload timed out" : "Failed to upload picture");
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingBanner(true);

    try {
      const storageRef = ref(storage, `hr-banners/${user.id}/${Date.now()}`);

      const uploadPromise = uploadBytes(storageRef, file);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Upload timed out")), 60000)
      );

      await Promise.race([uploadPromise, timeoutPromise]);
      const downloadURL = await getDownloadURL(storageRef);

      await updateDoc(doc(db, "hrProfiles", user.id), {
        bannerUrl: downloadURL,
      });

      setProfileData((prev) => ({
        ...prev,
        banner: downloadURL,
      }));

      toast.success("Banner updated successfully!");
      setShowBannerModal(false);
    } catch (error) {
      console.error("Banner upload error:", error);
      toast.error(error.message === "Upload timed out" ? "Upload timed out" : "Failed to upload banner");
    } finally {
      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }
    }
  };

  const handleManualProfilePictureSubmit = async () => {
    if (!manualUrl) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      const downloadURL = manualUrl;

      const auth = getAuth(firebaseApp);
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          photoURL: downloadURL,
        });
      }

      await updateDoc(doc(db, "hrProfiles", user.id), {
        profilePictureURL: downloadURL,
      });

      // Sync with recruiters collection
      try {
        await setDoc(doc(db, "recruiters", user.id), {
          avatar: downloadURL
        }, { merge: true });
      } catch (err) {
        console.error("Failed to sync avatar to recruiters:", err);
      }

      setProfileData((prev) => ({
        ...prev,
        avatar: downloadURL,
      }));

      if (updateUserProfile) {
        updateUserProfile({
          avatar: downloadURL
        });
      }

      toast.success("Profile picture updated!");
      setShowManualPictureModal(false);
      setManualUrl('');
    } catch (error) {
      console.error("Error updating profile picture:", error);
      toast.error("Failed to update profile picture");
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSaveProfile = async () => {
    try {
      const expertiseArray = editFormData.expertise
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      await updateDoc(doc(db, "hrProfiles", user.id), {
        name: editFormData.name,
        username: editFormData.username,
        title: editFormData.title,
        location: editFormData.location,
        company: editFormData.company,
        bio: editFormData.bio,
        phone: editFormData.phone,
        expertise: expertiseArray,
        updatedAt: new Date(),
      });

      // Sync core fields to recruiters (Primary Source)
      try {
        await setDoc(doc(db, "recruiters", user.id), {
          name: editFormData.name,
          title: editFormData.title,
          company: editFormData.company,
          location: editFormData.location,
          phone: editFormData.phone,
        }, { merge: true });
      } catch (err) {
        console.error("Failed to sync profile to recruiters:", err);
      }

      setProfileData({
        ...profileData,
        ...editFormData,
        expertise: expertiseArray,
      });

      // Update global user context to sync name with Dashboard immediately
      if (updateUserProfile) {
        updateUserProfile({
          name: editFormData.name
        });
      }

      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (passwordData.oldPassword === passwordData.newPassword) {
      toast.error("New password must be different from old password");
      return;
    }

    setPasswordLoading(true);

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.email) {
        toast.error("User not authenticated");
        return;
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.oldPassword
      );

      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, passwordData.newPassword);

      toast.success("Password updated successfully!");
      setShowPasswordModal(false);
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Password change error:", error);
      if (error.code === "auth/wrong-password") {
        toast.error("Current password is incorrect");
      } else if (error.code === "auth/requires-recent-login") {
        toast.error("Please log out and log in again to change password");
      } else {
        toast.error("Failed to change password. Please try again.");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Header Section with Cover Image */}
          <div className="relative group">
            {/* Cover Image / Gradient Background */}
            <div className="h-48 w-full overflow-hidden relative">
              {profileData?.banner ? (
                <img
                  src={profileData.banner}
                  alt="Profile Banner"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-red-900 via-red-800 to-red-600 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                </div>
              )}
              {/* Dark overlay on hover for better visibility of edit button */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
            </div>

            {/* Banner Action Buttons */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={() => setShowBannerModal(true)}
                className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg hover:bg-white hover:scale-105 transition-all text-gray-700 hover:text-red-600 border border-white/50"
                title="Edit Banner"
              >
                <Edit3 className="w-5 h-5" />
              </button>
            </div>

            {/* Profile Picture */}
            <div className="absolute -bottom-16 left-8">
              <div className="relative">
                <div className="w-32 h-32 rounded-2xl bg-white p-1.5 shadow-xl rotate-0 transition-transform hover:rotate-2">
                  <div className="w-full h-full rounded-xl overflow-hidden relative group cursor-pointer bg-gray-100">
                    {profileData?.avatar && !imageError ? (
                      <img
                        src={profileData.avatar}
                        alt={profileData.name}
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center text-red-700 text-4xl font-bold">
                        {getInitials(profileData?.name)}
                      </div>
                    )}

                    {/* Upload Overlay */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center"
                    >
                      <Edit3 className="w-8 h-8 text-white drop-shadow-lg" />
                    </div>

                    {uploadingPicture && (
                      <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm rounded-xl">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  disabled={uploadingPicture}
                  className="hidden"
                />
              </div>
            </div>

            {/* Manual URL Trigger */}
            <div className="absolute -bottom-14 left-44 block animate-fadeIn">
              <div className="flex gap-2">
                <div className="bg-white/90 backdrop-blur-md px-3 py-2 rounded-xl shadow-sm border border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <div>
                    <span className="font-bold text-gray-800">Photo Guidelines</span>
                    <div className="text-[10px] text-gray-500">Max 5MB • Square</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowManualPictureModal(true)}
                  className="bg-white/90 backdrop-blur-md px-3 py-2 rounded-xl shadow-sm border border-gray-200 text-xs text-gray-600 flex items-center gap-2 hover:bg-gray-50 hover:text-red-600 transition-colors"
                >
                  <LinkIcon className="w-4 h-4" />
                  <span className="font-bold">Use URL</span>
                </button>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-20 px-8 pb-8">
            <div className="flex flex-col md:flex-row justify-between items-start gap-6">
              {/* Left Side: Name & Details */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center gap-3">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.name}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, name: e.target.value })
                        }
                        className="text-3xl font-bold text-gray-900 border-b-2 border-red-200 focus:border-red-600 focus:outline-none bg-transparent w-full max-w-md px-1"
                        placeholder="Your Name"
                      />
                    ) : (
                      <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{profileData?.name}</h1>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={editFormData.title}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, title: e.target.value })
                        }
                        className="text-lg text-gray-600 border-b border-gray-300 focus:border-red-500 focus:outline-none bg-transparent w-48 px-1"
                        placeholder="Job Title"
                      />
                      <span className="text-gray-400">at</span>
                      <input
                        type="text"
                        value={editFormData.company}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, company: e.target.value })
                        }
                        className="text-lg text-gray-600 border-b border-gray-300 focus:border-red-500 focus:outline-none bg-transparent w-48 px-1"
                        placeholder="Company Name"
                      />
                    </div>
                  ) : (
                    <p className="text-lg text-gray-600 font-medium mt-1">
                      {profileData?.title} <span className="text-gray-400 font-normal">at</span> {profileData?.company}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-500" />
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.location}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, location: e.target.value })
                        }
                        className="border-b border-gray-300 focus:border-red-500 focus:outline-none bg-transparent px-1 min-w-[150px]"
                        placeholder="Location"
                      />
                    ) : (
                      <span>{profileData?.location}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-red-500" />
                    <span>Joined {formatDate(profileData?.joinDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-red-500" />
                    <span>{profileData?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-red-500" />
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.phone}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, phone: e.target.value })
                        }
                        className="border-b border-gray-300 focus:border-red-500 focus:outline-none bg-transparent px-1 min-w-[150px]"
                        placeholder="Phone Number"
                      />
                    ) : (
                      <span>{profileData?.phone || "No phone added"}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side: Actions */}
              <div className="flex flex-col gap-3 w-full sm:w-auto mt-6 md:mt-0">
                {isEditing ? (
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveProfile}
                      className="flex-1 sm:flex-none px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                    <button
                      onClick={handleEditToggle}
                      className="flex-1 sm:flex-none px-6 py-2.5 bg-white text-gray-700 rounded-xl text-sm font-bold border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-3">
                      <button
                        onClick={handleEditToggle}
                        className="flex-1 px-5 py-2.5 bg-white text-gray-700 rounded-xl text-sm font-bold border-2 border-gray-100 hover:border-red-100 hover:bg-red-50/50 hover:text-red-700 shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                      >
                        <Edit3 className="w-4 h-4" />
                        Edit Profile
                      </button>
                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="flex-1 px-5 py-2.5 bg-white text-gray-700 rounded-xl text-sm font-bold border-2 border-gray-100 hover:border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                      >
                        Password
                      </button>
                    </div>

                    <button
                      onClick={() => navigate("/hr/subscription")}
                      className="w-full px-6 py-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white rounded-xl text-sm font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2 relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                      <span className="relative z-10 flex items-center gap-2">Subscription Plan</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Experience */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Building className="w-5 h-5 text-red-600" />
                  Experience
                </h2>
                {/* Could add 'Add Experience' button here if needed */}
              </div>

              {companyHistory.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                  <Building className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No experience history available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {companyHistory.map((exp) => (
                    <div key={exp.id} className="group relative bg-white border border-gray-100 p-4 rounded-xl hover:shadow-md hover:border-red-100 transition-all duration-300">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                              {exp.company}
                            </h3>
                            <span
                              className={`px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide ${exp.type === "Remote"
                                ? "bg-purple-50 text-purple-700 border border-purple-100"
                                : exp.type === "On-site"
                                  ? "bg-green-50 text-green-700 border border-green-100"
                                  : "bg-blue-50 text-blue-700 border border-blue-100"
                                }`}
                            >
                              {exp.type || "On-site"}
                            </span>
                          </div>
                          <p className="text-gray-600 font-medium text-sm">
                            {exp.position || "HR Manager"}
                          </p>
                        </div>
                        <div className="text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1 rounded-lg self-start">
                          {exp.duration || "Present"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bio Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-red-600" />
                About
              </h2>
              {isEditing ? (
                <textarea
                  value={editFormData.bio}
                  onChange={(e) => setEditFormData({ ...editFormData, bio: e.target.value })}
                  className="w-full min-h-[120px] p-4 text-gray-700 border border-gray-200 rounded-xl focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none resize-y"
                  placeholder="Tell us about yourself..."
                />
              ) : (
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {profileData?.bio || "No bio added yet."}
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Skills & Reviews */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Top Skills</h2>
              <div className="flex flex-col gap-2">
                {isEditing ? (
                  <>
                    <textarea
                      value={editFormData.expertise}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, expertise: e.target.value })
                      }
                      className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none resize-none bg-gray-50"
                      placeholder="Add skills separated by commas (e.g. Technical Recruiting, HR Strategy, Leadership)"
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Info className="w-3 h-3" /> Separate multiple skills with commas
                    </p>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {profileData?.expertise && profileData.expertise.length > 0 ? (
                      profileData.expertise.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-all cursor-default"
                        >
                          {skill}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm italic">No skills listed</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Reviews</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab("reviews")}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${activeTab === "reviews"
                      ? "bg-red-50 text-red-700"
                      : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setActiveTab("pending")}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${activeTab === "pending"
                      ? "bg-red-50 text-red-700"
                      : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    Pending
                  </button>
                </div>
              </div>

              {activeTab === "reviews" && (
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <div className="text-center py-6">
                      <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-2 text-gray-300">
                        <Share2 className="w-6 h-6" />
                      </div>
                      <p className="text-gray-400 text-sm">No reviews yet</p>
                    </div>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-red-100 transition-colors">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold text-xs">
                            {getInitials(review.reviewerName || "User")}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-xs">
                              {review.reviewerName || "Anonymous"}
                            </h4>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                              {formatDate(review.createdAt)}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-600 text-xs leading-relaxed">
                          "{review.comment}"
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "pending" && (
                <p className="text-gray-400 text-center py-8 text-sm italic">No pending reviews</p>
              )}
            </div>
          </div>
        </div>

        {/* Banner Upload Modal */}
        {showBannerModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Update Profile Banner</h3>
                <button
                  onClick={() => setShowBannerModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex gap-3 text-blue-800">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm leading-relaxed">
                  <p className="font-semibold mb-1">Banner Guidelines</p>
                  <ul className="list-disc list-inside space-y-1 opacity-80">
                    <li>Recommended size: 1200x300 pixels</li>
                    <li>Maximum size: 5MB</li>
                    <li>Formats: JPG, PNG, WebP</li>
                    <li>Keep text content minimal</li>
                  </ul>
                </div>
              </div>

              <div
                onClick={() => bannerInputRef.current?.click()}
                className={`border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 hover:border-red-400 hover:bg-red-50 group relative overflow-hidden ${uploadingBanner ? 'pointer-events-none' : ''}`}
              >
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleBannerUpload}
                />
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-100 group-hover:text-red-600 transition-colors text-gray-400">
                  {uploadingBanner ? (
                    <div className="absolute inset-0 bg-white/90 z-20 flex flex-col items-center justify-center">
                      <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-red-600 font-semibold animate-pulse">Uploading...</p>
                    </div>
                  ) : (
                    <Upload className="w-8 h-8" />
                  )}
                </div>
                <p className="text-gray-900 font-semibold mb-1">Click to upload image</p>
                <p className="text-gray-400 text-sm">or drag and drop here</p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowBannerModal(false)}
                  className="px-6 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual URL Modal */}
        {showManualPictureModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Image URL</h3>
                <button
                  onClick={() => setShowManualPictureModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Paste a direct link to an image (e.g. from LinkedIn, Imgur).
              </p>
              <input
                type="text"
                placeholder="https://example.com/photo.jpg"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm mb-4 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleManualProfilePictureSubmit}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-red-700 transition"
                >
                  Save Photo
                </button>
                <button
                  onClick={() => setShowManualPictureModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-semibold text-sm hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modern Password Modal */}
        {
          showPasswordModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Change Password</h3>
                    <p className="text-gray-500 text-sm mt-1">Secure your account with a new password</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({
                        oldPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.oldPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, oldPassword: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-500 transition-all outline-none"
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, newPassword: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-500 transition-all outline-none"
                      placeholder="Enter new password"
                    />
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" /> Minimum 6 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-500 transition-all outline-none"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <button
                    onClick={handlePasswordChange}
                    disabled={passwordLoading}
                    className="flex-1 px-6 py-3.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex justify-center items-center"
                  >
                    {passwordLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : "Update Password"}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({
                        oldPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                    }}
                    className="px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </div >
    </div >
  );
};

export default HRNewProfile;
